import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface LanguageSwitcherProps {
  variant?: "light" | "dark";
  className?: string;
}

const LANGUAGES = [
  { code: "en", short: "EN", nameKey: "language.en", flag: "🇬🇧" },
  { code: "hy", short: "ՀԱՅ", nameKey: "language.hy", flag: "🇦🇲" },
  { code: "ru", short: "РУС", nameKey: "language.ru", flag: "🇷🇺" },
];

export default function LanguageSwitcher({ variant = "dark", className = "" }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isDark = variant === "dark";
  const currentCode = (i18n.resolvedLanguage || i18n.language || "en").slice(0, 2);
  const current = LANGUAGES.find((lang) => lang.code === currentCode) ?? LANGUAGES[0];

  useEffect(() => {
    document.documentElement.lang = currentCode;
  }, [currentCode]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleSelect = (code: string) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("language.label")}
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer border transition ${
          isDark
            ? "text-foreground-800 border-background-300 hover:border-primary-400 hover:text-primary-600 bg-background-50/60"
            : "text-background-50 border-background-50/30 hover:border-primary-300 bg-background-50/10 backdrop-blur"
        }`}
      >
        <i className="ri-global-line text-base w-4 h-4 flex items-center justify-center" />
        <span className="uppercase tracking-wide">{current.short}</span>
        <i
          className={`ri-arrow-down-s-line text-base w-4 h-4 flex items-center justify-center transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 mt-2 w-44 bg-background-50 border border-background-200 rounded-lg overflow-hidden z-[60]"
        >
          {LANGUAGES.map((lang) => {
            const isActive = lang.code === currentCode;
            return (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => handleSelect(lang.code)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-left cursor-pointer transition ${
                  isActive
                    ? "bg-primary-50 text-primary-800"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                <span className="text-base leading-none w-5 flex items-center justify-center">{lang.flag}</span>
                <span className="flex-1">{t(lang.nameKey)}</span>
                {isActive && (
                  <i className="ri-check-line text-primary-600 w-4 h-4 flex items-center justify-center" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}